import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { demoRequestService } from '../../services/api';
import { DEMO_STATUSES, statusLabel, statusClass, deploymentLabel } from './demoStatus';
import '../Trials/TrialDetail.css'; // shared layout styles (trial-card, trial-grid, ...)

const dt = (d) => (d ? format(new Date(d), 'dd MMM yyyy, HH:mm') : '—');
// <input type="datetime-local"> wants local time without a zone suffix.
const inputDateTime = (d) => (d ? format(new Date(d), "yyyy-MM-dd'T'HH:mm") : '');

function Section({ title, rows }) {
  return (
    <div className="card trial-card">
      <h3>{title}</h3>
      <dl className="trial-kv">
        {rows.map(([k, v, mono]) => (<React.Fragment key={k}><dt>{k}</dt><dd className={mono ? 'mono' : undefined}>{v || '—'}</dd></React.Fragment>))}
      </dl>
    </div>
  );
}

export default function DemoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dr, setDr] = useState(null);
  const [demoDate, setDemoDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await demoRequestService.getById(id);
    setDr(res.data.request);
    setDemoDate(inputDateTime(res.data.request.demoDate));
  }, [id]);
  useEffect(() => { load().catch(() => setError('Demo request not found')); }, [load]);

  const patch = async (body) => {
    setSaving(true); setError('');
    try { await demoRequestService.update(id, body); await load(); }
    catch (err) { setError(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    await demoRequestService.addNote(id, note.trim());
    setNote(''); load();
  };

  if (!dr) return <div className="empty-state">{error || 'Loading...'}</div>;

  return (
    <div className="trial-detail">
      <button className="btn btn-ghost" onClick={() => navigate('/demos')}><ArrowLeft size={14} /> Back to demo requests</button>
      <div className="page-header" style={{ marginTop: 16 }}>
        <div>
          <h1 className="page-title">{dr.company}</h1>
          <p className="page-subtitle"><span className="ticket-num">{dr.reference}</span> · {dr.product} · submitted {dt(dr.createdAt)}</p>
        </div>
        <button className="btn btn-ghost" disabled={saving || !!dr.assignedTo} onClick={() => patch({ assignToMe: true })}>
          {dr.assignedTo ? `Assigned to ${dr.assignedTo.name}` : 'Assign to me'}
        </button>
      </div>

      {error && <div className="trial-error">{error}</div>}

      <div className="card trial-card">
        <h3>Handling</h3>
        <div className="trial-handling">
          <label>Status
            <select value={dr.status} disabled={saving} onChange={e => patch({ status: e.target.value })}>
              {DEMO_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>Demo date &amp; time
            <input type="datetime-local" value={demoDate} onChange={e => setDemoDate(e.target.value)} />
          </label>
          <button className="btn btn-primary" disabled={saving} onClick={() => patch({ demoDate: demoDate ? new Date(demoDate).toISOString() : null })}>Save date</button>
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
          Marking a demo as scheduled requires a demo date. Current: <span className={`badge ${statusClass(dr.status)}`}>{statusLabel(dr.status)}</span>
        </div>
      </div>

      <div className="trial-grid">
        <Section title="Contact" rows={[
          ['Name', dr.contactName], ['Designation', dr.designation], ['Email', dr.email], ['Phone', dr.phone],
        ]} />
        <Section title="Request" rows={[
          ['Company', dr.company], ['Preferred deployment', deploymentLabel(dr.deployment)],
          ['About their mill', dr.message], ['Demo date', dt(dr.demoDate)],
        ]} />
        <Section title="Submission record" rows={[
          ['Source', dr.source], ['Origin', dr.origin], ['IP address', dr.submittedIp, true], ['Submitted at', dt(dr.createdAt), true],
        ]} />
      </div>

      <div className="card trial-card" style={{ marginTop: 16 }}>
        <h3>Internal notes</h3>
        {(dr.notes || []).length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No notes yet.</div>}
        {(dr.notes || []).map((n, i) => (
          <div key={i} className="trial-note"><div className="trial-note__meta">{n.authorName} · {dt(n.createdAt)}</div>{n.text}</div>
        ))}
        <form onSubmit={addNote} style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add an internal note (not visible to the prospect)" />
          <div><button className="btn btn-primary" type="submit" disabled={!note.trim()}>Add note</button></div>
        </form>
      </div>

      {(dr.statusHistory || []).length > 0 && (
        <div className="card trial-card">
          <h3>Status history</h3>
          {dr.statusHistory.map((h, i) => (<div key={i} style={{ fontSize: 13, padding: '2px 0' }}>{dt(h.at)} · <strong>{statusLabel(h.status)}</strong> · {h.by}</div>))}
        </div>
      )}
    </div>
  );
}
